/**
 * Role Permission Service
 * Handles role permission management with backend API
 */

import { apiGet, apiPost, apiPut } from '@/shared/api/apiClient';

/**
 * Permission Catalog DTO
 */
export interface PermissionCatalogDto {
  id: string;
  module: string;
  moduleName: string;
  action: string;
  actionName: string;
  displayName: string;
  description?: string;
  isActive: boolean;
}

export interface CreatePermissionCatalogRequest {
  module: number;
  action: number;
  displayName: string;
  description?: string;
  userId: string;
}

export interface UpdatePermissionCatalogStatusRequest {
  isActive: boolean;
}

/**
 * Permission Matrix Item
 */
export interface PermissionMatrixItemDto {
  permissionCatalogId: string;
  module: string;
  moduleName: string;
  action: string;
  actionName: string;
  displayName: string;
  isGranted: boolean;
}

/**
 * Role Permissions Response
 */
export interface RolePermissionsDto {
  roleId: string;
  roleName: string;
  permissions: PermissionMatrixItemDto[];
}

/**
 * Save Role Permissions Request
 */
export interface SaveRolePermissionsRequest {
  roleId: string;
  tenantId: string;
  userId: string;
  permissions: { permissionCatalogId: string; isGranted: boolean }[];
}

/**
 * User Permissions Response
 */
export interface UserPermissionsDto {
  userId: string;
  roleId: string;
  roleName: string;
  permissions: string[]; // ["attendance.view", "leave.approve"]
}

export interface UserPermissionMatrixItemDto {
  permissionCatalogId: string;
  module: string;
  moduleName: string;
  action: string;
  actionName: string;
  displayName: string;
  roleIsGranted: boolean;
  userOverrideIsGranted: boolean | null;
  effectiveIsGranted: boolean;
}

export interface UserPermissionMatrixDto {
  userId: string;
  roleId: string;
  roleName: string;
  permissions: UserPermissionMatrixItemDto[];
}

export interface SaveUserPermissionOverridesRequest {
  userId: string;
  tenantId: string;
  actorUserId: string;
  permissions: { permissionCatalogId: string; isGranted: boolean | null }[];
}

/**
 * Get permission catalog
 */
export async function getCatalog(): Promise<PermissionCatalogDto[]> {
  return apiGet<PermissionCatalogDto[]>('/rolepermissions/catalog');
}

/**
 * Create new permission catalog item
 */
export async function createPermissionCatalog(data: CreatePermissionCatalogRequest): Promise<PermissionCatalogDto> {
  return apiPost<PermissionCatalogDto>('/rolepermissions/catalog', data);
}

/**
 * Update catalog permission active status
 */
export async function updatePermissionCatalogStatus(permissionCatalogId: string, data: UpdatePermissionCatalogStatusRequest): Promise<void> {
  await apiPut<void>(`/rolepermissions/catalog/${permissionCatalogId}/status`, data);
}

/**
 * Get role permissions matrix
 */
export async function getRolePermissions(roleId: string, tenantId: string): Promise<RolePermissionsDto> {
  return apiGet<RolePermissionsDto>(`/rolepermissions/${roleId}?tenantId=${tenantId}`);
}

/**
 * Save role permissions
 */
export async function saveRolePermissions(data: SaveRolePermissionsRequest): Promise<void> {
  await apiPost<void>('/rolepermissions/save', data);
}

/**
 * Get user permissions (called after login)
 */
export async function getUserPermissions(userId: string, tenantId: string): Promise<UserPermissionsDto> {
  return apiGet<UserPermissionsDto>(`/rolepermissions/user/${userId}?tenantId=${tenantId}`);
}

/**
 * Get effective user permission matrix (role + user overrides)
 */
export async function getUserPermissionMatrix(userId: string, tenantId: string): Promise<UserPermissionMatrixDto> {
  return apiGet<UserPermissionMatrixDto>(`/rolepermissions/user/${userId}/matrix?tenantId=${tenantId}`);
}

/**
 * Save user-level permission overrides
 */
export async function saveUserPermissionOverrides(userId: string, data: SaveUserPermissionOverridesRequest): Promise<void> {
  await apiPost<void>(`/rolepermissions/user/${userId}/overrides`, data);
}
